import { RootState } from '@next/core-lib/app';
import { DataSeries } from '@next/core-lib/models';
import { SettingsDataObject } from '@next/core-lib/settings';
import { EntityState } from '@ngrx/entity';

export interface RefValSheetModuleState extends RootState {
  refValSheets?: RefValSheetState;
  isLoaded: boolean;
  columns: string[];
  refValSheetDataSeries?: ReferenceValueSheetDataSeries[];
  refValSheetKPIResults?: ReferenceValueSheetKPIResults[];
}

export type RefValSheetState = EntityState<ReferenceValueSheet>;

export interface ReferenceValueSheet extends SettingsDataObject {
  id: number;
  locationId: number;
  updatedAdvisorProfileId: number;
  createdAdvisorProfileId: number;
  updatedAdvisorName: string;
  createdAdvisorName: string;
  name: string;
  description: string;
  isActive: boolean;
  updated: string;
  created: string;
  country?: string;
  isStandardReferenceValueSheet: boolean;
  standardReferenceValueSheetId?: number;
  series: ReferenceValueSheetSeries;
  referenceValueSheetRows: ReferenceValueSheetRow[];
  temperatureUnit: TemperatureUnit;
  feedUnit: FeedUnit;
  waterUnit: WaterUnit;
  weightUnit: WeightUnit;
  animalType: AnimalType;
}

export interface ReferenceValueSheetSeries extends SettingsDataObject {
  temperatureInside: boolean;
  relativeHumidity: boolean;
  feedIntakePerChickPerDay: boolean;
  waterIntake: boolean;
  weight: boolean;
  mortality: boolean;
  numberOfHoursLight: boolean;
  lightIntensityInLux: boolean;
}

export interface ReferenceValueSheetRow extends SettingsDataObject {
  id: number;
  day: number;
  temperatureInside?: number;
  relativeHumidity?: number;
  feedIntakePerChickPerDay?: number;
  waterIntake?: number;
  weight?: number;
  mortality?: number;
  numberOfHoursLight?: number;
  lightIntensityInLux?: number;
}

export interface GetReferenceValueSheetsResponse {
  referenceValueSheets: ReferenceValueSheet[];
}

export interface GetReferenceValueSheetKPIsResponse {
  kpis: ReferenceValueSheetKPI[];
}

export interface ReferenceValueSheetKPIResults {
  refvalsheetId: number;
  kpis: ReferenceValueSheetKPI[];
}
export interface ReferenceValueSheetKPI {
  kpi: string;
  results: Array<{
    interval: number;
    intervalType: string;
    value: number;
  }>;
}

export interface UpsertRefValSheetResponse {
  id?: number;
}

export enum ViewMode {
  View,
  Create,
  Edit,
}

export enum AnimalType {
  BROILER = 'Broiler',
  REARING = 'Rearing',
}

export enum TemperatureUnit {
  Celcius = 'Celcius',
  Fahrenheit = 'Fahrenheit',
}

export enum FeedUnit {
  Gram = 'Gram',
  Kilogram = 'Kilogram',
}

export enum WaterUnit {
  ML = 'ML',
  DL = 'DL',
  L = 'L',
}

export enum WeightUnit {
  Gram = 'Gram',
  Kilogram = 'Kilogram',
}

export enum RVSSettings {
  temperatureUnit = TemperatureUnit.Celcius,
  feedUnit = FeedUnit.Gram,
  waterUnit = WaterUnit.ML,
  weightUnit = WeightUnit.Gram,
}

export class ColumnSettings {
  public static readonly DAY = 'day';
  public static readonly TEMPERATURE_INSIDE = 'temperatureInside';
  public static readonly RELATIVE_HUMIDITY = 'relativeHumidity';
  public static readonly FEED_INTAKE = 'feedIntakePerChickPerDay';
  public static readonly WATER_INTAKE = 'waterIntake';
  public static readonly WEIGHT = 'weight';
  public static readonly MORTALITY = 'mortality';
  public static readonly LIGHT_HOURS = 'numberOfHoursLight';
  public static readonly LUX = 'lightIntensityInLux';
  public static readonly DELETE_ROW = 'deleteRow';
  public static readonly COLUMN_SETTINGS_ARRAY_DEFAULT = [
    ColumnSettings.DAY,
    ColumnSettings.TEMPERATURE_INSIDE,
    ColumnSettings.RELATIVE_HUMIDITY,
    ColumnSettings.FEED_INTAKE,
    ColumnSettings.WATER_INTAKE,
    ColumnSettings.WEIGHT,
    ColumnSettings.MORTALITY,
    ColumnSettings.LIGHT_HOURS,
    ColumnSettings.LUX,
  ];
}

export interface ReferenceValueSheetDataSeries {
  id: number;
  name: string;
  series: DataSeries<number, number>;
}
